/* eslint-disable import/no-relative-packages */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import FroalaEditorComponent from "react-froala-wysiwyg";
import FroalaEditor from "froala-editor";
import { YouTubeIcon } from "Components/Icons";
import { renderToString } from "react-dom/server";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/languages/en_gb.js";
import "froala-editor/js/languages/sv.js";
import "froala-editor/js/languages/fi.js";
import "froala-editor/js/languages/nb.js";
import { CircularProgress } from "Components/Base/Feedback/CircularProgress";
import config from "../../../Portal/web/Content/js/lib/config";
import {
  getLicenseKey,
  getToolbars,
  getFontFamilies,
  getFontSizes,
} from "./functions";
import { getLocaleCode } from "./utils";
import { TOOLBAR_TYPES } from "./constants";

require("./style.css");

const urlRoot = window.location.origin;
const urlPathName = window.location.pathname;
const { culture } = config;

// Determine resource browser URL based on pathname
const getResourceBrowserUrl = () => {
  if (urlPathName.toLowerCase().includes("luvitadmin")) {
    return "LuvitAdmin/Repository/selectFileForFroalaEditor.aspx?";
  }
  return "LuvitPortal/EnterAdmin.aspx?AdminPage=selectFileForFroalaEditor";
};

const resourceBrowserUrl = getResourceBrowserUrl();

/**
 * A WYSIWYG editor component using Froala Editor
 * @param {Object} props - Component props
 * @param {string} [props.toolbar=TOOLBAR_TYPES.FULL] - The toolbar type to use
 * @param {string} [props.height='400px'] - Height of the editor
 * @param {string} [props.value=''] - Initial content value
 * @param {Function} props.onChange - Callback when content changes
 * @returns {React.Component} Froala WYSIWYG editor component
 */
const Wysiwyg = ({
  toolbar = TOOLBAR_TYPES.FULL,
  height = "400px",
  value = "",
  onChange,
}) => {
  const language = getLocaleCode(culture);
  const editorRef = useRef();
  const [editorState, setEditorState] = useState({
    editorInitialized: false,
    licenseKey: "",
  });

  const registerCustomButtons = () => {
    FroalaEditor.ICON_TEMPLATES.custom_insertVideo = renderToString(
      <YouTubeIcon />,
    );
    FroalaEditor.DefineIcon("insertVideo", {
      template: "custom_insertVideo",
    });

    FroalaEditor.RegisterCommand("insertImage", {
      title: "Insert Image",
      icon: "insertImage",
      focus: false,
      undo: true,
      refreshAfterCallback: false,
      callback: () => {
        window.open(
          `${urlRoot}/${resourceBrowserUrl}&FroalaEditorID=${editorRef.current.editor.id}`,
          "_blank",
          `popup=yes`,
        );
      },
    });
  };

  useEffect(() => {
    const initialize = async () => {
      const licenseKey = await getLicenseKey();
      registerCustomButtons();
      window.setFroalaImage = (url) => {
        const currentEditor = editorRef.current;
        if (!currentEditor?.editor) {
          return;
        }
        const encodedUrl = encodeURI(url);
        currentEditor.editor.image.insert(encodedUrl, true, null, null);
      };
      setEditorState({ editorInitialized: true, licenseKey });
    };

    initialize();

    return () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.destroy();
      }
    };
  }, []);

  if (!editorState.editorInitialized) {
    return <CircularProgress />;
  }

  return (
    <FroalaEditorComponent
      config={{
        ...getToolbars({ toolbarName: toolbar }),
        fontFamily: getFontFamilies(),
        fontSize: getFontSizes(),
        language,
        height,
        key: editorState.licenseKey,
        htmlRemoveTags: ["script"],
      }}
      ref={editorRef}
      onModelChange={onChange}
      model={value}
    />
  );
};

Wysiwyg.propTypes = {
  toolbar: PropTypes.oneOf(Object.values(TOOLBAR_TYPES)),
  height: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export { TOOLBAR_TYPES, Wysiwyg };
